import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ArrowUpRight } from "lucide-react";
import SEO, { orgJsonLd } from "../components/SEO";
import Hero from "../components/Hero";
import ClientsMarquee from "../components/ClientsMarquee";
import StatsBar, { SectionHead } from "../components/StatsBar";
import Reveal, { Eyebrow } from "../components/Reveal";
import Testimonials from "../components/Testimonials";
import LeadForm from "../components/LeadForm";
import Icon from "../components/Icon";
import { SERVICES, INDUSTRIES, STATES, CASE_STUDIES } from "../data/siteData";

const CHAPTERS = [
  { num: "01", title: "Start", body: "Incorporation, licences and registrations bundled into one sprint. You get a compliant, bank-ready entity while your competitors are still collecting documents." },
  { num: "02", title: "Scale", body: "Virtual CFO, accounting, tax, audit and ROC compliance run as one operating system — the financial discipline that turns revenue into a fundable story." },
  { num: "03", title: "Raise", body: "Registered-valuer reports, investor-grade decks and warm introductions to our capital network. We've supported ₹550+ Cr of valuations across India." },
];

export default function HomePage() {
  return (
    <>
      <SEO
        title="Complisure Counsultancy | Company Registration, Virtual CFO & Fundraising Advisory India"
        description="CA, CS & Legal advisory for startups and SMEs — company registration, Virtual CFO, business valuation and fundraising advisory across 31 Indian cities."
        canonical="https://compliance-capital-1.preview.emergentagent.com/"
        jsonLd={[orgJsonLd]}
      />
      <Hero />
      <ClientsMarquee />
      <StatsBar />

      {/* Manifesto chapters */}
      <section data-testid="manifesto-section" className="relative py-24">
        <div className="dot-grid absolute inset-0 opacity-[0.08]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead
            eyebrow="The Complisure Way"
            title="One partner for the entire company journey"
            sub="Most firms do filings. We run the full arc — from the day you incorporate to the day you sign a term sheet."
          />
          <div className="grid gap-6 md:grid-cols-3">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12}>
                <div data-testid={`chapter-${c.num}`} className="glass card-lift group relative overflow-hidden rounded-3xl p-8">
                  <span className="font-display text-6xl font-extrabold text-brand-blue/10 transition-colors duration-300 group-hover:text-brand-blue/20">{c.num}</span>
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-brand-navy">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-gray">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section data-testid="services-section" className="relative bg-brand-ice/50 py-24">
        <div className="blob blob-float right-[-5%] top-0 h-80 w-80 bg-brand-blue/10" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead
            eyebrow="Services"
            title="Everything between an idea and a term sheet"
            sub="Eleven practice areas, one engagement manager, zero dropped deadlines."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="glass card-lift group flex h-full flex-col rounded-3xl p-7"
                >
                  <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-brand-sky text-white shadow-lg shadow-brand-blue/25">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold tracking-tight text-brand-navy group-hover:text-brand-blue">{s.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-gray">{s.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue transition-all group-hover:gap-3">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation spotlight */}
      <section data-testid="valuation-spotlight" className="relative overflow-hidden py-24">
        <div className="blob blob-float-slow left-[-10%] bottom-[-20%] h-[30rem] w-[30rem] bg-brand-blue/10" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Valuation & Fundraising</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">
              ₹550+ Cr valued. <span className="text-gradient">800+ clients.</span> One obsession: your round.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-brand-gray sm:text-lg">
              IBBI-registered valuer reports, DCF and comparable analysis, investor-grade pitch decks and introductions to angels, VCs and family offices — a complete fundraising stack for early-stage and growth-stage companies preparing to raise capital.
            </p>
            <ul className="mt-6 space-y-3">
              {["Registered valuer & merchant banker network", "5-step process: Discovery → Data Room → Report → Pitch → Term Sheet", "40+ professionals, 15+ legal advisors, 26+ years combined experience"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-brand-navy/80">
                  <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/valuation-fundraising" data-testid="valuation-spotlight-cta" className="btn-gradient mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25">
              Get a Valuation Report <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="glass card-lift overflow-hidden rounded-[28px] p-2.5">
              <img
                src="https://images.unsplash.com/photo-1568658173325-c7b8a11d5666?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBhZHZpc29yJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBzdGFydHVwJTIwZm91bmRlcnN8ZW58MHx8fHwxNzg4Nzg2MTEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Advisory team reviewing a fundraising valuation"
                className="h-80 w-full rounded-[20px] object-cover"
                loading="lazy"
              />
              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="font-display text-sm font-bold text-brand-navy">Latest close</p>
                  <p className="text-xs text-brand-gray">B2B SaaS · Hyderabad · Series A</p>
                </div>
                <span className="rounded-full bg-brand-ice px-4 py-2 font-display text-sm font-extrabold text-brand-blue">₹58 Cr</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Industries */}
      <section data-testid="industries-section" className="bg-brand-navy py-24 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead
            eyebrow="Industries We Serve"
            title="Fluent in your industry's numbers"
            sub={<span className="text-white/60">From SaaS metrics to factory cost audits — we speak the dialect of your P&L.</span>}
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.08}>
                <div data-testid={`industry-card-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="glass-dark card-lift h-full rounded-3xl p-7">
                  <Icon name={ind.icon} className="mb-4 h-7 w-7 text-brand-sky" />
                  <h3 className="font-display text-lg font-bold">{ind.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{ind.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Locations preview */}
      <section data-testid="locations-preview" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead
            eyebrow="Where We Work"
            title="31 cities. 8 states. One standard."
            sub="Local desks with local compliance fluency — backed by a national network of CAs, CSs and legal advisors."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STATES.map((st, i) => (
              <Reveal key={st.slug} delay={(i % 4) * 0.07}>
                <Link to={`/locations/${st.slug}`} data-testid={`state-card-${st.slug}`} className="glass card-lift group block h-full rounded-3xl p-6">
                  <MapPin className="mb-3 h-5 w-5 text-brand-blue" />
                  <h3 className="font-display text-lg font-bold text-brand-navy group-hover:text-brand-blue">{st.name}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-brand-gray">{st.tagline}</p>
                  <p className="mt-3 flex flex-wrap gap-1.5">
                    {st.cities.slice(0, 3).map((c) => (
                      <span key={c.slug} className="rounded-full bg-brand-ice px-2.5 py-1 text-[11px] font-medium text-brand-blue">{c.name}</span>
                    ))}
                    {st.cities.length > 3 && <span className="rounded-full bg-brand-ice/60 px-2.5 py-1 text-[11px] font-medium text-brand-gray">+{st.cities.length - 3}</span>}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Case study strip */}
      <section data-testid="case-strip" className="bg-brand-ice/50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead eyebrow="Proof" title="Outcomes, not activity reports" sub="A sample of what happens when compliance, finance and fundraising run as one system." />
          <div className="grid gap-5 md:grid-cols-2">
            {CASE_STUDIES.slice(0, 2).map((cs, i) => (
              <Reveal key={cs.client} delay={i * 0.1}>
                <div data-testid={`case-card-${cs.client.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="glass card-lift h-full rounded-3xl p-8">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full bg-brand-ice px-3 py-1 text-xs font-semibold text-brand-blue">{cs.industry}</span>
                    <span className="font-display text-sm font-extrabold text-brand-navy">{cs.amount}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-brand-navy">{cs.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-gray">{cs.summary}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/case-studies" data-testid="cases-view-all" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:gap-3 transition-all">
              View all case studies <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section data-testid="home-cta-section" className="relative py-24">
        <div className="blob blob-float left-[10%] top-0 h-80 w-80 bg-brand-blue/10" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Free Consultation</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">
              Talk to a senior advisor this week — <span className="text-gradient">not a sales rep.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-gray sm:text-lg">
              Thirty minutes, zero obligation. You'll leave with a clear compliance and growth roadmap whether or not we work together.
            </p>
            <div className="mt-6 flex items-center gap-4 text-sm text-brand-gray">
              <span className="flex items-center gap-2"><Icon name="Phone" className="h-4 w-4 text-brand-blue" /> +91 73510 10107</span>
              <span className="flex items-center gap-2"><Icon name="Mail" className="h-4 w-4 text-brand-blue" /> hello@complisure.in</span>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <LeadForm sourcePage="/" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
